/**
 * Pure (JSX-free, DOM-free) reading rules for the thread: where a day begins,
 * and whether the stream should follow new content. Kept out of `ChatStream` so
 * both are unit-tested with no DOM and no clock.
 */
import type { MessageView } from '../../api/types.ts';

/** One rendered row: a persisted turn, or the separator that opens its day. */
export type ThreadRow =
  | { readonly kind: 'day'; readonly label: string }
  | { readonly kind: 'message'; readonly message: MessageView };

/**
 * Interleave day separators into the history.
 *
 * History renders as a flat column, so a thread spanning three days reads as one
 * run-on conversation — the reader cannot tell last Tuesday from this morning.
 * A separator is emitted whenever a message opens a calendar day different from
 * the one before it, INCLUDING the first, so the top of the thread is dated too.
 *
 * The day is computed in the USER's timezone, not the viewer's or UTC: the same
 * instant is Monday night in one zone and Tuesday morning in the next, and the
 * separator has to agree with the timestamps rendered on the bubbles right
 * beneath it. That is why `formatDay` is INJECTED rather than imported — the
 * caller passes the same `formatDateTime` binding, with the same user timezone
 * and locale, that it stamps the bubbles with, so the boundary cannot drift from
 * the label. It also keeps this module free of `@4f/ui`, whose barrel pulls JSX.
 *
 * The formatted date doubles as the grouping KEY: one call answers both "which
 * day is this" and "what does the separator read", so the two can never disagree.
 */
export function withDaySeparators(
  messages: readonly MessageView[],
  formatDay: (iso: string) => string,
): ThreadRow[] {
  const rows: ThreadRow[] = [];
  let openDay: string | null = null;
  for (const message of messages) {
    const label = formatDay(message.createdAt);
    if (label !== openDay) {
      rows.push({ kind: 'day', label });
      openDay = label;
    }
    rows.push({ kind: 'message', message });
  }
  return rows;
}

/**
 * How close to the bottom still counts as "following the conversation", in CSS
 * pixels. Generous on purpose: sub-pixel scroll heights, a composer growing by a
 * line, and momentum scrolling all leave a few pixels of slack, and treating
 * that slack as "the user scrolled away" would strand a reader who never moved.
 */
export const FOLLOW_THRESHOLD_PX = 120;

/** The scroll geometry the follow decision reads — a plain object, so it is testable. */
export interface ScrollGeometry {
  readonly scrollTop: number;
  readonly scrollHeight: number;
  readonly clientHeight: number;
}

/**
 * Whether new content should scroll into view.
 *
 * `ChatStream` anchored unconditionally on every token while its own doc comment
 * promised "unless the user has scrolled up" — so scrolling back to re-read
 * something mid-run yanked you to the bottom on the very next token, which in a
 * streaming reply is a few times a second. The scrollback was unusable exactly
 * when there was most to read.
 *
 * A thread shorter than its viewport reports `scrollHeight <= clientHeight` and
 * is trivially at the bottom, which is the correct answer: an empty or one-turn
 * conversation must still follow.
 */
export function shouldFollow(
  geometry: ScrollGeometry,
  threshold: number = FOLLOW_THRESHOLD_PX,
): boolean {
  const distanceFromBottom = geometry.scrollHeight - geometry.scrollTop - geometry.clientHeight;
  return distanceFromBottom <= threshold;
}
